import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../../i18n'
import { recomputeExecutiveGovernance } from '../../../services/executiveGovernanceApi'
import {
  fetchExecutivePostureMatrix,
  fetchExecutiveRuntimeTimeline,
  type PostureMatrixRow,
  type RuntimeTimelineEvent,
} from '../../../services/executivePostureApi'
import CompliancePostureMatrix from './CompliancePostureMatrix'
import RuntimeGovernanceTimeline from './RuntimeGovernanceTimeline'
import entStyles from '../../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import widgetStyles from '../../../styles/pages/home/ExecutivePostureWidgets.module.scss'
import { Button } from '../../ui/Button'

const HOME_TIMELINE_PREF_KEY = 'defendsphere.home.showRuntimeTimeline'

function readShowRuntimeTimeline(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(HOME_TIMELINE_PREF_KEY) === 'true'
}

type Props = {
  standardCodes?: string[]
}

export default function HomeExecutivePostureWidgetsRow({ standardCodes = [] }: Props) {
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [recomputing, setRecomputing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [matrixRows, setMatrixRows] = useState<PostureMatrixRow[]>([])
  const [timelineEvents, setTimelineEvents] = useState<RuntimeTimelineEvent[]>([])
  const [showRuntimeTimeline, setShowRuntimeTimeline] = useState(readShowRuntimeTimeline)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const matrix = await fetchExecutivePostureMatrix(standardCodes)
      setMatrixRows(matrix?.rows || [])
      if (showRuntimeTimeline) {
        const timeline = await fetchExecutiveRuntimeTimeline(10)
        setTimelineEvents(timeline?.events || [])
      } else {
        setTimelineEvents([])
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load executive posture widgets')
      setMatrixRows([])
      setTimelineEvents([])
    } finally {
      setLoading(false)
    }
  }, [standardCodes.join(','), showRuntimeTimeline])

  useEffect(() => {
    void load()
  }, [load])

  const handleRecompute = useCallback(async () => {
    setRecomputing(true)
    try {
      await recomputeExecutiveGovernance()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to recompute governance posture')
    } finally {
      setRecomputing(false)
    }
  }, [load])

  return (
    <section
      className={`${entStyles.layer} ${entStyles.onDarkSurface}`}
      data-testid="home-executive-posture-widgets"
    >
      <p className={entStyles.layerLabel}>
        {t('home.executivePosture.layerLabel', 'Executive operational governance')}
      </p>
      {error ? <p className={widgetStyles.widgetsError}>{error}</p> : null}
      <div style={{ marginBottom: 8 }}>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            const next = !showRuntimeTimeline
            setShowRuntimeTimeline(next)
            window.localStorage.setItem(HOME_TIMELINE_PREF_KEY, next ? 'true' : 'false')
          }}
        >
          {showRuntimeTimeline
            ? t('home.kpi.hideTimeline', 'Hide runtime timeline')
            : t('home.kpi.showTimeline', 'Show runtime timeline')}
        </Button>
      </div>
      <div className={widgetStyles.widgetsGrid}>
        <CompliancePostureMatrix rows={matrixRows} loading={loading} />
        {showRuntimeTimeline ? (
          <RuntimeGovernanceTimeline
            events={timelineEvents}
            loading={loading}
            recomputing={recomputing}
            onRecompute={() => void handleRecompute()}
          />
        ) : null}
      </div>
    </section>
  )
}
